// apps/web/src/pages/estoque/inventario-detalhe-page.tsx — Fase 13, Plano 27 (EST-13, D-24, D-25, D-26).
// Detalhe do inventário em modo CONTAGEM CEGA (`/estoque/inventarios/detalhe?id=`): a tela só consome `ItemContagemCega`
// (sem saldo do sistema; o servidor nem envia). "Salvar rascunho" grava só as linhas que mudaram, com a `versao`; "Enviar
// contagem" salva o que falta, confirma e envia para aprovação por OUTRA pessoa. Depois do envio o contador não vê
// diferenças. Plano 13-29: quem tem `estoque:aprovar-ajuste` vê a REVISÃO (GET :id/revisao, nunca antes) e aprova os
// ajustes com justificativa por linha, desde que não tenha participado da contagem (a recusa final é do servidor).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, CheckCircle2, Hourglass, Info, ListChecks, Loader2, Plus, RefreshCw, User, XCircle } from 'lucide-react';
import type { InventarioContagemView, LoteLookupContagemEstoque, MaterialLookupPv, ResultadoAprovacaoInventario, RevisaoInventarioView } from '@pieltec/contracts';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Button } from '../../components/ui/button';
import { LoteCombobox } from '../../components/estoque/lote-combobox';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { FormMessage } from '../../components/ui/form-message';
import { Skeleton } from '../../components/ui/skeleton';
import { MaterialCombobox } from '../../components/vendas/material-combobox';
import { apiMutate } from '../../lib/csrf-fetch';
import {
  PERMISSAO_APROVAR_AJUSTE,
  PERMISSAO_INVENTARIAR,
  apresentacaoSituacaoInventario,
  edicaoInicial,
  errosDeLeitura,
  itensAlterados,
  lerErroInventario,
  lotesDeSalvamento,
  mesclarEdicao,
  pedeCustoInicial,
  percentualProgresso,
  progressoDaEdicao,
  temAlteracoesNaoSalvas,
  textoConfirmacaoEnvio,
  textoProgresso,
  urlDetalheInventario,
  validarEnvio,
  type EdicaoContagem,
  type EdicaoLinha,
  type ErroInventario,
  type ErroLinha,
  type IconeSituacaoInventario,
} from '../../lib/estoque/inventario';
import {
  MENSAGEM_PARTICIPANTE_NAO_VE_REVISAO,
  ehAutorDaContagem,
  podeAprovar,
  podeVerRevisao as podeVerRevisaoDoInventario,
  justificativasIniciais,
  justificativasPendentes,
  montarAprovar,
  movimentosGerados,
  resumoRevisao,
  textoParticipacao,
  textoParticipantes,
  textoResumoRevisao,
  textoSucessoAprovacao,
  type JustificativasRevisao,
} from '../../lib/estoque/inventario-revisao';
import { obterSessaoAtual } from '../../lib/session';
import { formatarDataHora } from '../../lib/vendas/formatacao';
import { ContagemGrade } from './contagem-grade';
import { AprovarAjustesDialog, DescartarContagemDialog } from './inventario-dialogs';
import { RevisaoGrade } from './revisao-grade';

const ICONES_SITUACAO: Record<IconeSituacaoInventario, typeof ListChecks> = { ListChecks, Hourglass, CheckCircle2, XCircle };
const URL_LISTA = '/estoque/inventarios';

type EstadoCarga = 'carregando' | 'erro' | 'nao-encontrado' | 'ok';

function rotaInventario(id: string, sufixo: string): string {
  return `/api/v1/estoque/inventarios/${encodeURIComponent(id)}${sufixo}`;
}

const ENDPOINT_MATERIAIS_ESTOQUE = '/api/v1/estoque/lookups/materiais';

type MaterialEscolhido = MaterialLookupPv & { unidade?: string };

interface AdicionarItemDialogProps {
  readonly aberto: boolean;
  readonly inventario: InventarioContagemView;
  readonly aoFechar: () => void;
  readonly aoAdicionado: (view: InventarioContagemView) => void;
}

/** Item fora da lista (só em inventário sem filtro de itens): matéria-prima na Pieltec, lote no inventário de cliente. */
function AdicionarItemDialog({ aberto, inventario, aoFechar, aoAdicionado }: AdicionarItemDialogProps) {
  const ehCliente = inventario.donoTipo === 'CLIENTE';
  const [material, setMaterial] = useState<MaterialEscolhido | null>(null);
  const [lote, setLote] = useState<LoteLookupContagemEstoque | null>(null);
  const [erro, setErro] = useState<ErroInventario | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setMaterial(null);
    setLote(null);
    setErro(null);
  }, [aberto]);

  const escolhido = ehCliente ? lote !== null : material !== null;

  async function adicionar(): Promise<void> {
    if (enviando || !escolhido) return;
    setEnviando(true);
    setErro(null);
    try {
      const corpo = ehCliente ? { loteId: lote?.id } : { materialId: material?.id };
      const res = await apiMutate(rotaInventario(inventario.id, '/itens'), { body: corpo });
      if (!res.ok) {
        setErro(await lerErroInventario(res));
        return;
      }
      aoAdicionado((await res.json()) as InventarioContagemView);
    } catch {
      setErro({
        status: 0,
        codigo: null,
        mensagem: 'Não foi possível adicionar o item. Verifique a conexão e tente novamente.',
        conflitoDeVersao: false,
        inventarioConflitante: null,
        itemCodigo: null,
        itemId: null,
      });
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(proximo) => !proximo && !enviando && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adicionar item não listado</DialogTitle>
          <DialogDescription>
            {ehCliente ? 'Escolha o lote de cliente que está na prateleira e não aparece na lista.' : 'Escolha a matéria-prima que está na prateleira e não aparece na lista.'}
          </DialogDescription>
        </DialogHeader>
        {ehCliente ? (
          <LoteCombobox<LoteLookupContagemEstoque> id="inv-adicionar-item" value={lote} onChange={setLote} endpoint={rotaInventario(inventario.id, '/lotes-nao-listados')} />
        ) : (
          <MaterialCombobox<MaterialEscolhido> id="inv-adicionar-item" endpoint={ENDPOINT_MATERIAIS_ESTOQUE} value={material} onChange={setMaterial} />
        )}
        {erro && (
          <div role="alert" className="flex flex-col gap-2">
            <FormMessage>{erro.mensagem}</FormMessage>
            {erro.inventarioConflitante && (
              <Button asChild variant="outline" className="min-h-11 self-start sm:min-h-9">
                <a href={urlDetalheInventario(erro.inventarioConflitante.id)}>Abrir inventário {erro.inventarioConflitante.numero}</a>
              </Button>
            )}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" className="min-h-11 sm:min-h-9" disabled={enviando} onClick={aoFechar}>
            Voltar
          </Button>
          <Button type="button" className="min-h-11 sm:min-h-9" disabled={enviando || !escolhido} onClick={() => void adicionar()}>
            {enviando && <Loader2 className="animate-spin" aria-hidden="true" />}
            Adicionar item
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function InventarioDetalhePage() {
  const id = new URLSearchParams(window.location.search).get('id');
  const [estado, setEstado] = useState<EstadoCarga>(id ? 'carregando' : 'nao-encontrado');
  const [view, setView] = useState<InventarioContagemView | null>(null);
  const [edicao, setEdicao] = useState<EdicaoContagem>({});
  const [erros, setErros] = useState<Readonly<Record<string, ErroLinha>>>({});
  const [permissoes, setPermissoes] = useState<readonly string[]>([]);
  const [mensagemGeral, setMensagemGeral] = useState<string | null>(null);
  const [rascunhoSalvo, setRascunhoSalvo] = useState(false);
  const [erroApi, setErroApi] = useState<ErroInventario | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [adicionando, setAdicionando] = useState(false);
  const [usuarioId, setUsuarioId] = useState<string | null>(null);
  const [sessaoLida, setSessaoLida] = useState(false);
  const [revisao, setRevisao] = useState<RevisaoInventarioView | null>(null);
  const [estadoRevisao, setEstadoRevisao] = useState<'parado' | 'carregando' | 'erro' | 'bloqueado' | 'ok'>('parado');
  const [justificativas, setJustificativas] = useState<JustificativasRevisao>({});
  const [itensComErro, setItensComErro] = useState<ReadonlySet<string>>(new Set());
  const [aprovando, setAprovando] = useState(false);
  const [confirmandoAprovar, setConfirmandoAprovar] = useState(false);
  const [erroRevisao, setErroRevisao] = useState<ErroInventario | null>(null);
  const [sucessoAprovacao, setSucessoAprovacao] = useState<string | null>(null);
  const [descartando, setDescartando] = useState(false);
  const [enviandoDescarte, setEnviandoDescarte] = useState(false);
  const [erroDescarte, setErroDescarte] = useState<ErroInventario | null>(null);

  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (!vivo) return;
      setPermissoes(sessao.autenticado ? sessao.usuario.permissions : []);
      setUsuarioId(sessao.autenticado ? sessao.usuario.userId : null);
      setSessaoLida(true);
    });
    return () => {
      vivo = false;
    };
  }, []);

  const carregar = useCallback(
    async (sinal?: { vivo: boolean }) => {
      if (!id) return;
      setEstado('carregando');
      try {
        const res = await fetch(rotaInventario(id, ''), { credentials: 'include' });
        if (res.status === 404) {
          if (!sinal || sinal.vivo) setEstado('nao-encontrado');
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as InventarioContagemView;
        if (!sinal || sinal.vivo) {
          setView(json);
          setEdicao(edicaoInicial(json.itens));
          setErros({});
          setEstado('ok');
        }
      } catch {
        if (!sinal || sinal.vivo) setEstado('erro');
      }
    },
    [id],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar]);

  // A revisão (saldo esperado, diferença) só é pedida a quem tem estoque:aprovar-ajuste e só depois do envio (T-13-29B);
  // a API também recusa (403) e durante a contagem ninguém recebe esses números.
  const situacaoAtual = view?.situacao ?? null;
  const versaoAtual = view?.versao ?? null;
  // CR-C03: quem contou não vê a revisão nem descarta a contagem. A lista de autores vem da visão; quem só enviou sem contar
  // não aparece nela e é barrado pelo servidor (409 INVENTARIO_AUTOAPROVACAO), o que vira o estado 'bloqueado'.
  const autoresIds = (view?.autoresContagem ?? []).map((autor) => autor.userId);
  const autorDaContagem = ehAutorDaContagem(usuarioId, autoresIds);
  const aprovadorComPermissao = permissoes.includes(PERMISSAO_APROVAR_AJUSTE);
  const podeVerRevisao = podeVerRevisaoDoInventario({ usuarioId, autoresContagemIds: autoresIds, permissoes });
  const carregarRevisao = useCallback(
    async (sinal?: { vivo: boolean }) => {
      if (!id) return;
      setEstadoRevisao('carregando');
      try {
        const res = await fetch(rotaInventario(id, '/revisao'), { credentials: 'include' });
        if (res.status === 409 && (await lerErroInventario(res)).codigo === 'INVENTARIO_AUTOAPROVACAO') {
          if (!sinal || sinal.vivo) {
            setRevisao(null);
            setEstadoRevisao('bloqueado');
          }
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as RevisaoInventarioView;
        if (sinal && !sinal.vivo) return;
        setRevisao(json);
        setJustificativas(justificativasIniciais(json.itens));
        setItensComErro(new Set());
        setEstadoRevisao('ok');
      } catch {
        if (!sinal || sinal.vivo) setEstadoRevisao('erro');
      }
    },
    [id],
  );

  useEffect(() => {
    if (!sessaoLida || !podeVerRevisao || situacaoAtual === null || situacaoAtual === 'EM_CONTAGEM') return;
    const sinal = { vivo: true };
    void carregarRevisao(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [sessaoLida, podeVerRevisao, situacaoAtual, versaoAtual, carregarRevisao]);

  function alterarJustificativa(itemId: string, texto: string): void {
    setJustificativas((atual) => ({ ...atual, [itemId]: texto }));
    setItensComErro((atual) => {
      if (!atual.has(itemId)) return atual;
      const proximo = new Set(atual);
      proximo.delete(itemId);
      return proximo;
    });
    setErroRevisao(null);
  }

  async function confirmarAprovacao(): Promise<void> {
    if (!revisao || !id || aprovando) return;
    setAprovando(true);
    setErroRevisao(null);
    try {
      const res = await apiMutate(rotaInventario(id, '/aprovar'), { body: montarAprovar(revisao.versao, revisao.itens, justificativas) });
      if (!res.ok) {
        const erro = await lerErroInventario(res);
        setErroRevisao(erro);
        if (erro.itemId) setItensComErro(new Set([erro.itemId]));
        setConfirmandoAprovar(false);
        return;
      }
      const resultado = (await res.json()) as ResultadoAprovacaoInventario;
      setSucessoAprovacao(textoSucessoAprovacao(resultado));
      setConfirmandoAprovar(false);
      // A tela vira leitura do inventário ajustado: recarrega a visão e a revisão (movimentos gerados).
      void carregar();
    } catch {
      setErroRevisao({
        status: 0,
        codigo: null,
        mensagem: 'Não foi possível aprovar os ajustes. Verifique a conexão e tente novamente.',
        conflitoDeVersao: false,
        inventarioConflitante: null,
        itemCodigo: null,
        itemId: null,
      });
      setConfirmandoAprovar(false);
    } finally {
      setAprovando(false);
    }
  }

  /** Descarta a contagem inteira (nenhum movimento é gerado). Em erro o diálogo fica aberto e o motivo digitado é mantido. */
  async function confirmarDescarte(motivo: string): Promise<void> {
    if (!revisao || !id || enviandoDescarte) return;
    setEnviandoDescarte(true);
    setErroDescarte(null);
    try {
      const res = await apiMutate(rotaInventario(id, '/descartar'), { body: { versao: revisao.versao, motivo } });
      if (!res.ok) {
        setErroDescarte(await lerErroInventario(res));
        return;
      }
      setView((await res.json()) as InventarioContagemView);
      setErroRevisao(null);
      setDescartando(false);
    } catch {
      setErroDescarte({
        status: 0,
        codigo: null,
        mensagem: 'Não foi possível descartar a contagem. Verifique a conexão e tente novamente.',
        conflitoDeVersao: false,
        inventarioConflitante: null,
        itemCodigo: null,
        itemId: null,
      });
    } finally {
      setEnviandoDescarte(false);
    }
  }

  function alterarLinha(itemId: string, parcial: Partial<EdicaoLinha>): void {
    setEdicao((atual) => {
      const base = atual[itemId] ?? { quantidade: '', custo: '' };
      return { ...atual, [itemId]: { ...base, ...parcial } };
    });
    setErros((atual) => {
      if (!atual[itemId]) return atual;
      const { [itemId]: _removido, ...restante } = atual;
      return restante;
    });
    setRascunhoSalvo(false);
    setMensagemGeral(null);
    setErroApi(null);
  }

  /**
   * Grava as linhas que mudaram (PUTs de até 500, cada um sobe a versão). Devolve a view atualizada ou null em erro:
   * nesse caso nada digitado é descartado.
   */
  async function gravarAlteracoes(base: InventarioContagemView, comCusto: boolean): Promise<InventarioContagemView | null> {
    if (!id) return null;
    let atual = base;
    for (const lote of lotesDeSalvamento(itensAlterados(base.itens, edicao, { comCusto }))) {
      const res = await apiMutate(rotaInventario(id, '/contagem'), { method: 'PUT', body: { versao: atual.versao, itens: lote } });
      if (!res.ok) {
        setErroApi(await lerErroInventario(res));
        // Lotes já gravados mudaram a versão: o diff seguinte é calculado contra a view mais nova.
        if (atual !== base) setView(atual);
        return null;
      }
      atual = (await res.json()) as InventarioContagemView;
    }
    if (atual !== base) {
      setView(atual);
      setEdicao(edicaoInicial(atual.itens));
    }
    return atual;
  }

  async function salvarRascunho(): Promise<void> {
    if (!view || salvando || enviando) return;
    const comCusto = pedeCustoInicial(view);
    const leitura = errosDeLeitura(view.itens, edicao, comCusto);
    setErros(leitura);
    setRascunhoSalvo(false);
    setErroApi(null);
    if (Object.keys(leitura).length > 0) {
      setMensagemGeral('Corrija os campos destacados antes de salvar.');
      return;
    }
    setMensagemGeral(null);
    setSalvando(true);
    try {
      const salvo = await gravarAlteracoes(view, comCusto);
      if (salvo) setRascunhoSalvo(true);
    } catch {
      setMensagemGeral('Não foi possível salvar o rascunho. Verifique a conexão e tente novamente.');
    } finally {
      setSalvando(false);
    }
  }

  function pedirEnvio(): void {
    if (!view) return;
    const resultado = validarEnvio(view, edicao);
    setErros(resultado.erros);
    setRascunhoSalvo(false);
    setErroApi(null);
    setMensagemGeral(resultado.mensagem);
    if (resultado.ok) setConfirmando(true);
  }

  async function confirmarEnvio(): Promise<void> {
    if (!view || !id || enviando) return;
    setEnviando(true);
    setErroApi(null);
    try {
      const salvo = await gravarAlteracoes(view, pedeCustoInicial(view));
      if (!salvo) {
        setConfirmando(false);
        return;
      }
      const res = await apiMutate(rotaInventario(id, '/enviar'), { body: { versao: salvo.versao } });
      if (!res.ok) {
        setErroApi(await lerErroInventario(res));
        setConfirmando(false);
        return;
      }
      const enviado = (await res.json()) as InventarioContagemView;
      setView(enviado);
      setEdicao(edicaoInicial(enviado.itens));
      setConfirmando(false);
    } catch {
      setMensagemGeral('Não foi possível enviar a contagem. Verifique a conexão e tente novamente.');
      setConfirmando(false);
    } finally {
      setEnviando(false);
    }
  }

  const podeInventariar = permissoes.includes(PERMISSAO_INVENTARIAR);

  // Aviso do navegador ao sair (fechar, voltar, menu) com contagem digitada e ainda não salva.
  const pendente = useMemo(
    () => view !== null && view.situacao === 'EM_CONTAGEM' && temAlteracoesNaoSalvas(view.itens, edicao, pedeCustoInicial(view)),
    [view, edicao],
  );
  useEffect(() => {
    if (!pendente) return;
    const avisar = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [pendente]);

  /** Só aqui o que foi digitado e não salvo é descartado: o usuário clicou em "Recarregar inventário". */
  function recarregar(): void {
    setErroApi(null);
    setErroRevisao(null);
    setErroDescarte(null);
    setDescartando(false);
    setMensagemGeral(null);
    setRascunhoSalvo(false);
    void carregar();
  }

  function aoAdicionarItem(atualizada: InventarioContagemView): void {
    setView(atualizada);
    setEdicao((atual) => mesclarEdicao(atual, atualizada.itens));
    setAdicionando(false);
    setRascunhoSalvo(false);
    setMensagemGeral(null);
    setErroApi(null);
  }

  if (estado === 'carregando') {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6" aria-busy="true" aria-label="Carregando inventário">
        <Card>
          <CardContent className="flex flex-col gap-2">
            <Skeleton className="h-6 w-80" />
            <Skeleton className="h-4 w-56" />
          </CardContent>
        </Card>
        <div className="flex flex-col gap-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (estado === 'nao-encontrado') {
    return (
      <div className="mx-auto w-full max-w-7xl p-6">
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <h1 className="text-xl font-semibold leading-tight">Inventário não encontrado</h1>
            <p className="text-sm text-muted-foreground">Confira o endereço ou volte para a lista de inventários.</p>
            <Button asChild variant="outline" className="min-h-11 sm:min-h-9">
              <a href={URL_LISTA}>Voltar aos inventários</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (estado === 'erro' || !view) {
    return (
      <div className="mx-auto w-full max-w-7xl p-6">
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-rose-700">Não foi possível carregar o inventário. Verifique a conexão e tente novamente.</p>
            <Button type="button" variant="outline" onClick={() => void carregar()}>
              <RefreshCw aria-hidden="true" />
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const situacao = apresentacaoSituacaoInventario(view.situacao);
  const IconeSituacao = ICONES_SITUACAO[situacao.icone];
  const emContagem = view.situacao === 'EM_CONTAGEM';
  const comCusto = pedeCustoInicial(view);
  const andamento = progressoDaEdicao(view.itens, edicao);
  const exibido = emContagem ? andamento : view.progresso;
  const validacaoEnvio = emContagem ? validarEnvio(view, edicao) : null;

  // Revisão e aprovação (Plano 13-29): a regra de segregação é aplicada aqui só para explicar; o servidor recusa de qualquer forma.
  const participantes = textoParticipantes(view.autoresContagem, view.enviadoPorNome);
  const participacao = textoParticipacao(view.autoresContagem, usuarioId, formatarDataHora);
  const aprovadorLiberado = podeAprovar({ usuarioId, autoresContagemIds: autoresIds, permissoes });
  const movimentos = revisao && view.situacao === 'AJUSTADO' ? movimentosGerados(revisao.itens) : [];
  const resumo = revisao && view.situacao === 'AGUARDANDO_APROVACAO' ? resumoRevisao(revisao.itens) : null;
  const pendentes = revisao && view.situacao === 'AGUARDANDO_APROVACAO' ? justificativasPendentes(revisao.itens, justificativas) : [];

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">
      <Card>
        <CardContent className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-semibold leading-tight">
              Inventário {view.numero} — {view.localizacao.nome}
            </h1>
            <span
              className={
                view.donoTipo === 'PIELTEC'
                  ? 'inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700'
                  : 'inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2 py-0.5 text-xs font-medium text-slate-700'
              }
            >
              {view.donoTipo === 'PIELTEC' ? <Building2 className="size-3.5" aria-hidden="true" /> : <User className="size-3.5" aria-hidden="true" />}
              {view.donoNome}
            </span>
            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${situacao.classes}`}>
              <IconeSituacao className="size-3.5 shrink-0" aria-hidden="true" />
              {situacao.rotulo}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold tabular-nums" aria-live="polite">
              {textoProgresso(exibido)}
            </p>
            <div
              role="progressbar"
              aria-label="Progresso da contagem"
              aria-valuemin={0}
              aria-valuemax={exibido.total}
              aria-valuenow={exibido.contados}
              className="h-1.5 w-full max-w-md overflow-hidden rounded-full bg-slate-200"
            >
              <div className="h-full bg-blue-600" style={{ width: `${percentualProgresso(exibido)}%` }} />
            </div>
          </div>
        </CardContent>
      </Card>

      {emContagem && view.eAbertura && (
        <div role="status" className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <p>Esta localização ainda não tem movimentos. Esta contagem será o saldo de abertura: cada item gera uma entrada inicial com o custo informado.</p>
        </div>
      )}

      {emContagem ? (
        <>
          <p className="text-sm text-muted-foreground">Contagem cega: o saldo do sistema não é exibido. Conte o que está na prateleira.</p>

          {view.itens.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">Este inventário não tem itens para contar.</CardContent>
            </Card>
          ) : (
            <Card className="overflow-hidden py-0 gap-0">
              <ContagemGrade itens={view.itens} edicao={edicao} aoAlterar={alterarLinha} comCusto={comCusto} erros={erros} desabilitada={!podeInventariar || salvando || enviando} />
            </Card>
          )}

          <div role="status" aria-live="polite">
            {rascunhoSalvo && <p className="text-sm font-semibold text-emerald-700">Rascunho salvo.</p>}
          </div>
          {(mensagemGeral || erroApi) && (
            <Alert variant="destructive">
              <AlertDescription>
                <div className="flex flex-wrap items-center gap-3">
                  <span>{erroApi ? erroApi.mensagem : mensagemGeral}</span>
                  {erroApi?.conflitoDeVersao && (
                    <Button type="button" variant="outline" className="min-h-11 bg-white sm:min-h-9" onClick={recarregar}>
                      <RefreshCw aria-hidden="true" />
                      Recarregar inventário
                    </Button>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          <div className="sticky bottom-0 z-20 flex flex-wrap items-center justify-end gap-2 border-t bg-background py-3">
            {podeInventariar && !view.filtroItens && (
              <Button type="button" variant="ghost" className="mr-auto min-h-11 sm:min-h-9" disabled={salvando || enviando} onClick={() => setAdicionando(true)}>
                <Plus aria-hidden="true" />
                Adicionar item não listado
              </Button>
            )}
            <Button asChild variant="outline" className="min-h-11 sm:min-h-9">
              <a href={URL_LISTA}>Voltar</a>
            </Button>
            {podeInventariar && (
              <>
                <Button type="button" variant="secondary" className="min-h-11 sm:min-h-9" disabled={salvando || enviando} onClick={() => void salvarRascunho()}>
                  {salvando && <Loader2 className="animate-spin" aria-hidden="true" />}
                  Salvar rascunho
                </Button>
                <Button type="button" className="min-h-11 sm:min-h-9" disabled={salvando || enviando || (validacaoEnvio?.contados ?? 0) === 0} onClick={pedirEnvio}>
                  Enviar contagem
                </Button>
              </>
            )}
          </div>
        </>
      ) : (
        <>
          {sucessoAprovacao && view.situacao === 'AJUSTADO' && (
            <div role="status" className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm font-semibold text-emerald-700">
              <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <p>{sucessoAprovacao}</p>
            </div>
          )}
          <Card>
            <CardContent className="flex flex-col gap-2 py-6">
              {view.situacao === 'AGUARDANDO_APROVACAO' && !podeVerRevisao && (
                <>
                  <p className="text-base font-semibold">Contagem enviada. Aguardando aprovação de outra pessoa.</p>
                  <p className="text-sm text-muted-foreground">Aguardando aprovação de alguém que não participou da contagem.</p>
                </>
              )}
              {view.situacao === 'AGUARDANDO_APROVACAO' && podeVerRevisao && <p className="text-base font-semibold">Aguardando aprovação.</p>}
              {view.situacao === 'AJUSTADO' && <p className="text-base font-semibold">Inventário ajustado.</p>}
              {view.situacao === 'DESCARTADO' && <p className="text-base font-semibold">Inventário descartado. Nenhum ajuste foi gerado.</p>}
              {participantes.contagem && (
                <p className="text-sm">
                  Contagem por <span className="font-semibold">{participantes.contagem}</span>
                </p>
              )}
              {participantes.remetente && (
                <p className="text-sm">
                  Enviada por <span className="font-semibold">{participantes.remetente}</span>
                  {view.enviadoEm ? ` em ${formatarDataHora(view.enviadoEm)}` : ''}
                </p>
              )}
              {view.situacao === 'AGUARDANDO_APROVACAO' && podeVerRevisao && participacao && <p className="text-sm text-amber-700">{participacao}</p>}
              {!(view.situacao === 'AGUARDANDO_APROVACAO' && podeVerRevisao && estadoRevisao !== 'bloqueado') && (
                <div>
                  <Button asChild variant="outline" className="min-h-11 sm:min-h-9">
                    <a href={URL_LISTA}>Voltar</a>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {aprovadorComPermissao && (autorDaContagem || estadoRevisao === 'bloqueado') && (
            <Card>
              <CardContent role="status" className="py-8 text-center text-sm text-muted-foreground">
                {MENSAGEM_PARTICIPANTE_NAO_VE_REVISAO}
              </CardContent>
            </Card>
          )}

          {podeVerRevisao && estadoRevisao === 'carregando' && (
            <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando revisão">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          )}
          {podeVerRevisao && estadoRevisao === 'erro' && (
            <Card>
              <CardContent role="alert" className="flex flex-col items-center gap-4 py-8 text-center">
                <p className="text-sm text-rose-700">Não foi possível carregar a revisão do inventário. Verifique a conexão e tente novamente.</p>
                <Button type="button" variant="outline" onClick={() => void carregarRevisao()}>
                  <RefreshCw aria-hidden="true" />
                  Tentar novamente
                </Button>
              </CardContent>
            </Card>
          )}

          {podeVerRevisao && estadoRevisao === 'ok' && revisao && view.situacao === 'AGUARDANDO_APROVACAO' && resumo && (
            <>
              {revisao.itens.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center text-sm text-muted-foreground">Nenhum item foi contado neste inventário.</CardContent>
                </Card>
              ) : (
                <Card className="overflow-hidden py-0 gap-0">
                  <RevisaoGrade
                    itens={revisao.itens}
                    donoTipo={view.donoTipo}
                    justificativas={justificativas}
                    aoAlterarJustificativa={alterarJustificativa}
                    itensComErro={itensComErro}
                    desabilitada={aprovando || !aprovadorLiberado}
                  />
                </Card>
              )}

              {erroRevisao && (
                <Alert variant="destructive">
                  <AlertDescription>
                    <div className="flex flex-wrap items-center gap-3">
                      <span>{erroRevisao.mensagem}</span>
                      {erroRevisao.conflitoDeVersao && (
                        <Button type="button" variant="outline" className="min-h-11 bg-white sm:min-h-9" onClick={recarregar}>
                          <RefreshCw aria-hidden="true" />
                          Recarregar inventário
                        </Button>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              <div className="sticky bottom-0 z-20 flex flex-col gap-2 border-t bg-background py-3">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <p className="text-sm font-semibold tabular-nums" aria-live="polite">
                    {textoResumoRevisao(resumo)}
                  </p>
                  {pendentes.length > 0 && aprovadorLiberado && (
                    <p className="text-xs text-amber-700">
                      {pendentes.length === 1 ? '1 linha com diferença sem justificativa.' : `${pendentes.length} linhas com diferença sem justificativa.`}
                    </p>
                  )}
                  {!aprovadorLiberado && autorDaContagem && (
                    <p role="status" className="text-sm text-rose-700">
                      Quem fez a contagem não pode aprovar este ajuste.
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Button asChild variant="outline" className="mr-auto min-h-11 sm:min-h-9">
                    <a href={URL_LISTA}>Voltar</a>
                  </Button>
                  <Button type="button" variant="outline" className="min-h-11 sm:min-h-9" disabled={aprovando} onClick={() => { setErroDescarte(null); setDescartando(true); }}>
                    Descartar contagem
                  </Button>
                  <Button
                    type="button"
                    className="min-h-11 sm:min-h-9"
                    disabled={aprovando || !aprovadorLiberado || pendentes.length > 0 || revisao.itens.length === 0}
                    onClick={() => setConfirmandoAprovar(true)}
                  >
                    {aprovando && <Loader2 className="animate-spin" aria-hidden="true" />}
                    Aprovar ajustes
                  </Button>
                </div>
              </div>
            </>
          )}

          {podeVerRevisao && estadoRevisao === 'ok' && revisao && revisao.versao === view.versao && view.situacao === 'AJUSTADO' && (
            <Card className="overflow-hidden py-0 gap-0">
              <div className="px-4 py-3">
                <h2 className="text-base font-semibold">Movimentos gerados</h2>
              </div>
              {movimentos.length === 0 ? (
                <p className="px-4 pb-6 text-sm text-muted-foreground">Nenhum movimento foi necessário: a contagem coincidiu com o saldo.</p>
              ) : (
                <table className="w-full text-sm">
                  <caption className="sr-only">Movimentos gerados pelo inventário</caption>
                  <thead className="bg-slate-100 text-left">
                    <tr>
                      <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Item</th>
                      <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Tipo</th>
                      <th scope="col" className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider">Quantidade</th>
                      <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Justificativa</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movimentos.map((movimento) => (
                      <tr key={movimento.itemId} className="border-t align-top">
                        <td className="max-w-0 px-3 py-2">
                          <div className="font-semibold tabular-nums">{movimento.codigo}</div>
                          <div className="truncate text-muted-foreground" title={movimento.descricao}>{movimento.descricao}</div>
                        </td>
                        <td className="px-3 py-3">{movimento.tipo}</td>
                        <td className="px-3 py-3 text-right font-semibold tabular-nums">{movimento.quantidade}</td>
                        <td className="px-3 py-3 text-muted-foreground">{movimento.justificativa}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          )}

          {podeVerRevisao && estadoRevisao === 'ok' && revisao && revisao.versao === view.versao && view.situacao === 'DESCARTADO' && (
            <Card>
              <CardContent className="flex flex-col gap-1 py-6">
                <p className="text-sm font-semibold">Motivo do descarte</p>
                <p className="text-sm text-muted-foreground">{revisao.motivoDescarte ?? 'Motivo não registrado.'}</p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {podeInventariar && emContagem && !view.filtroItens && (
        <AdicionarItemDialog aberto={adicionando} inventario={view} aoFechar={() => setAdicionando(false)} aoAdicionado={aoAdicionarItem} />
      )}

      {revisao && view.situacao === 'AGUARDANDO_APROVACAO' && (
        <DescartarContagemDialog
          aberto={descartando}
          enviando={enviandoDescarte}
          erro={erroDescarte}
          aoConfirmar={(motivo) => void confirmarDescarte(motivo)}
          aoFechar={() => setDescartando(false)}
          aoRecarregar={recarregar}
        />
      )}

      {resumo && (
        <AprovarAjustesDialog aberto={confirmandoAprovar} resumo={resumo} enviando={aprovando} aoConfirmar={() => void confirmarAprovacao()} aoFechar={() => setConfirmandoAprovar(false)} />
      )}

      <AlertDialog open={confirmando} onOpenChange={(aberto) => !aberto && !enviando && setConfirmando(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enviar contagem</AlertDialogTitle>
            <AlertDialogDescription>{textoConfirmacaoEnvio(andamento.contados, andamento.total)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={enviando} className="min-h-11 sm:min-h-9">
              Voltar
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={enviando}
              className="min-h-11 sm:min-h-9"
              onClick={(event) => {
                event.preventDefault();
                void confirmarEnvio();
              }}
            >
              {enviando ? 'Enviando...' : 'Enviar contagem'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
